import {
  GlobalProductNames,
  GlobalProductsToHideSubProductName,
} from "@constants/products";
import { Step } from "@layouts/stepper";
import { SelectedSubProductDTO } from "@models/managed-accounts/entities/step/fee";
import {
  FeatureAnalysisRowDTO,
  SelectedProductDTO,
} from "@models/platform-analysis/entities/steps/feature";
import {
  FamilyMemberAccountDTO,
  FeeSubProductDTO,
} from "@models/platform-analysis/entities/steps/fee";
import { ProductRecommendationDTO } from "@models/platform-analysis/entities/steps/summary";
import { AnalysisDecisionStatus } from "@models/platform-analysis/enums/feature/decisionStatus";
import { AnalysisFeePortfolioSetupType } from "@models/platform-analysis/enums/fee/portfolioSetupType";
import { ProductsShortlistValidationStatus } from "@models/platform-analysis/enums/status";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { AnalysisSubStep } from "@models/platform-analysis/enums/subStep";
import { BusinessMetricAnalysisStep } from "@pages/platform-analysis-v2/_id/steps/business-metric/analysis";
import { FeeCalculatorStep } from "@pages/platform-analysis-v2/_id/steps/fee/calculator";
import { formatCurrencyWithFixedDecimals } from "@utils";
import { chain, isEmpty, orderBy, replace } from "lodash";
import { AnalysisMaxShortlistedConfigs } from "./configs";
import { AnalysisDynamicStepsOrder } from "./constants";
import { BusinessMetricsSelectionStep } from "./steps/business-metric/selection";
import { FeatureAnalysisStep } from "./steps/feature/analysis";
import { FeatureSelectionStep } from "./steps/feature/selection";
import { FeeAnalysis } from "./steps/fee/analysis";
import { AnalysisSetupStep } from "./steps/setup";
import { AnalysisSummaryStep } from "./steps/summary";

export const getAnalysisInitialSteps = ({
  steps,
  isSupplierGroup,
}: {
  steps: {
    [key in AnalysisStep]: {
      isHidden?: boolean;
      isUnHighlighted?: boolean;
      subSteps?: {
        [key in AnalysisSubStep]: {
          isHidden?: boolean;
        };
      };
    };
  };
  isSupplierGroup?: boolean;
}): Step[] => [
  {
    key: AnalysisStep.Setup,
    label: `${isSupplierGroup ? "Analysis" : "Review"} setup`,
    component: AnalysisSetupStep,
    hidden: steps[AnalysisStep.Setup].isHidden,
    unHighlighted: steps[AnalysisStep.Setup].isUnHighlighted,
  },
  {
    key: AnalysisStep.Feature,
    label: `Feature ${isSupplierGroup ? "analysis" : "review"}`,
    hidden: steps[AnalysisStep.Feature].isHidden,
    unHighlighted: steps[AnalysisStep.Feature].isUnHighlighted,
    subSteps: [
      {
        key: AnalysisSubStep.Selection,
        label: "Feature selection",
        component: FeatureSelectionStep,
        hidden: steps[AnalysisStep.Feature].subSteps?.Selection.isHidden,
      },
      {
        key: AnalysisSubStep.Analysis,
        label: "Feature analysis",
        component: FeatureAnalysisStep,
      },
    ],
  },
  {
    key: AnalysisStep.BusinessMetric,
    label: `Business metric ${isSupplierGroup ? "analysis" : "review"}`,
    hidden: steps[AnalysisStep.BusinessMetric].isHidden,
    unHighlighted: steps[AnalysisStep.BusinessMetric].isUnHighlighted,
    subSteps: [
      {
        key: AnalysisSubStep.Selection,
        label: "Business metric selection",
        component: BusinessMetricsSelectionStep,
        hidden: steps[AnalysisStep.BusinessMetric].subSteps?.Selection.isHidden,
      },
      {
        key: AnalysisSubStep.Analysis,
        label: "Business metric analysis",
        component: BusinessMetricAnalysisStep,
      },
    ],
  },
  {
    key: AnalysisStep.Fee,
    label: `Fee ${isSupplierGroup ? "analysis" : "review"}`,
    hidden: steps[AnalysisStep.Fee].isHidden,
    unHighlighted: steps[AnalysisStep.Fee].isUnHighlighted,
    subSteps: [
      {
        key: AnalysisSubStep.Selection,
        label: "Fee calculator",
        component: FeeCalculatorStep,
        hidden: steps[AnalysisStep.Fee].subSteps?.Selection.isHidden,
      },
      {
        key: AnalysisSubStep.Analysis,
        label: "Fee analysis",
        component: FeeAnalysis,
      },
    ],
  },
  {
    key: AnalysisStep.Summary,
    label: "Summary",
    component: AnalysisSummaryStep,
    hidden: steps[AnalysisStep.Summary].isHidden,
    unHighlighted: steps[AnalysisStep.Summary].isUnHighlighted,
  },
];

export const checkStepIsLastVisibleStep = ({
  analysisSteps,
  stepToCheck,
}: {
  analysisSteps?: Record<AnalysisStep, { isHidden?: boolean }>;
  stepToCheck: AnalysisStep;
}): boolean => {
  let foundStepIndex = 0;
  let lastVisibleStepIndex = 0;
  AnalysisDynamicStepsOrder.forEach((step, index) => {
    if (step === stepToCheck) foundStepIndex = index;
    const stepIsVisible = !analysisSteps?.[step]?.isHidden;
    if (stepIsVisible) lastVisibleStepIndex = index;
  });

  return foundStepIndex === lastVisibleStepIndex;
};

export const validateReviewSelectedProductsStatuses = ({
  selectedProducts,
  isLastVisibleStep,
}: {
  selectedProducts: SelectedProductDTO[];
  isLastVisibleStep: boolean;
}): {
  status: ProductsShortlistValidationStatus;
  numOfShortlisted: number;
  numOfDisqualified: number;
  numOfUndecided: number;
} => {
  const returnData = {
    status: ProductsShortlistValidationStatus.Valid,
    ...countSelectedProductsByDecisionStatus(selectedProducts),
  };

  const { numOfShortlisted, numOfUndecided } = returnData;

  // Check for no shortlisted products
  const noShortlisted = numOfShortlisted === 0;
  if (noShortlisted)
    return {
      ...returnData,
      status: ProductsShortlistValidationStatus.NoShortlisted,
    };

  // Check for shortlist limit exceeded only if on the last visible step
  const exceededLimit =
    numOfShortlisted >
    AnalysisMaxShortlistedConfigs.MaxProductsShortlistedAllowed;

  if (isLastVisibleStep && exceededLimit) {
    return {
      ...returnData,
      status: ProductsShortlistValidationStatus.ShortlistLimitExceeded,
    };
  }

  // Check for undecided products
  const hasUndecidedProducts = numOfUndecided > 0;
  if (hasUndecidedProducts)
    return {
      ...returnData,
      status: ProductsShortlistValidationStatus.HasUndecided,
    };

  // All validations passed
  return returnData;
};

export const countSelectedProductsByDecisionStatus = (
  selectedProducts: SelectedProductDTO[],
) => {
  let numOfShortlisted = 0;
  let numOfDisqualified = 0;
  let numOfUndecided = 0;

  for (const product of selectedProducts) {
    const { decisionStatus } = product;
    switch (decisionStatus) {
      case AnalysisDecisionStatus.Shortlisted:
        numOfShortlisted++;
        break;
      case AnalysisDecisionStatus.Disqualified:
        numOfDisqualified++;
        break;
      default:
        numOfUndecided++;
        break;
    }
  }

  return {
    numOfShortlisted,
    numOfDisqualified,
    numOfUndecided,
  };
};

export const shouldHideSubProductName = (
  setupType: AnalysisFeePortfolioSetupType,
  productName?: string,
) => {
  return (
    GlobalProductsToHideSubProductName.includes(productName ?? "") &&
    setupType === AnalysisFeePortfolioSetupType.SubProduct
  );
};

export const removeSubProductNameForSpecificProduct = <
  T extends FeeSubProductDTO | SelectedSubProductDTO,
>(
  subProducts: T[],
): T[] => {
  return subProducts.map((subProduct) => ({
    ...subProduct,
    name: shouldHideSubProductName(subProduct.setupType, subProduct.productName)
      ? ""
      : subProduct.name,
  }));
};

export const isAllExpandEssentialSubProductsHidden = ({
  hiddenSubProductIds,
  selectedSubProducts,
}: {
  hiddenSubProductIds: string[];
  selectedSubProducts: FeeSubProductDTO[];
}): boolean => {
  return selectedSubProducts
    ?.filter(
      (subProduct) =>
        subProduct.productName === GlobalProductNames.Expand &&
        !subProduct.isRateCard &&
        subProduct.name?.includes(GlobalProductNames.Essential),
    )
    ?.map((subProduct) => subProduct.id)
    ?.every((subProductId) =>
      hiddenSubProductIds?.some((id) => id === subProductId),
    );
};

export const getStringFamilyMemberAccounts = (
  data: FamilyMemberAccountDTO[],
): string => {
  return (
    chain(data)
      ?.map(
        (data) =>
          `${replace(
            data.variableName ? data.variableName : "",
            "_",
            " ",
          )}: ${formatCurrencyWithFixedDecimals({ value: data.balance })}`,
      )
      ?.join(", ")
      ?.value() ?? ""
  );
};

export const sortByProductOrder = <
  T extends {
    productId?: string;
    order?: number;
    recommendation?: Partial<ProductRecommendationDTO>;
  },
>({
  data,
  getItemId,
  productRecommendations,
}: {
  data: T[];
  getItemId: (item: T) => string;
  productRecommendations?: Record<string, Partial<ProductRecommendationDTO>>;
}): T[] => {
  if (!productRecommendations || isEmpty(data)) return data;

  const cloneData: T[] = structuredClone(data);

  return (
    cloneData
      ?.sort((a, b) => {
        return (
          (productRecommendations[getItemId(a)]?.order || 0) -
          (productRecommendations[getItemId(b)]?.order || 0)
        );
      })
      ?.map((item) => {
        const recommendation = productRecommendations[getItemId(item)];
        if (!recommendation) return item;

        return { ...item, recommendation };
      }) || []
  );
};

export const sortBySubProductOrder = <
  T extends {
    productId?: string;
    order?: number;
    recommendation?: Partial<ProductRecommendationDTO>;
  },
>({
  data,
  getProductId,
  productRecommendations,
}: {
  data: T[];
  getProductId: (item: T) => string;
  productRecommendations?: Record<string, Partial<ProductRecommendationDTO>>;
}): T[] => {
  if (!productRecommendations) return data;

  return orderBy(data, [
    (item) => {
      const productId = getProductId(item);

      return (
        productRecommendations?.[productId]?.order ?? Number.MAX_SAFE_INTEGER
      );
    },
    (item) => item.order ?? Number.MAX_SAFE_INTEGER,
    ["asc", "asc"],
  ])?.map((item) => ({
    ...item,
    recommendation: productRecommendations?.[item.productId || ""],
  }));
};

export const sortFeatureAnalysisRowsByProductOrder = ({
  originalRows,
  productRecommendations,
}: {
  originalRows: FeatureAnalysisRowDTO[];
  productRecommendations?: Record<string, Partial<ProductRecommendationDTO>>;
}) => {
  const getSortedData = (row: FeatureAnalysisRowDTO) => {
    return {
      analysisData: sortBySubProductOrder({
        data: row.analysisData,
        productRecommendations,
        getProductId: (item) => item.productId,
      }),
      MAAnalysisData: sortBySubProductOrder({
        data: row.MAAnalysisData || [],
        productRecommendations,
        getProductId: (item) => item.productId || "",
      }),
    };
  };

  return originalRows?.map((sectionRow) => {
    const { subRows: subSectionRows } = sectionRow;

    const {
      analysisData: sectionOrderedAnalysisData,
      MAAnalysisData: sectionOrderedMAAnalysisData,
    } = getSortedData(sectionRow);

    const sectionOrderedSubRows = subSectionRows?.map((subSectionRow) => {
      const { subRows: featureRows } = subSectionRow;

      const {
        analysisData: subSectionOrderedAnalysisData,
        MAAnalysisData: subSectionOrderedMAAnalysisData,
      } = getSortedData(subSectionRow);

      const orderedFeatureRows = featureRows?.map((featureRow) => {
        const {
          analysisData: orderedFeatureAnalysisData,
          MAAnalysisData: orderedFeatureMAAnalysisData,
        } = getSortedData(featureRow);

        return {
          ...featureRow,
          analysisData: orderedFeatureAnalysisData,
          MAAnalysisData: orderedFeatureMAAnalysisData,
        };
      });

      return {
        ...subSectionRow,
        analysisData: subSectionOrderedAnalysisData,
        MAAnalysisData: subSectionOrderedMAAnalysisData,
        subRows: orderedFeatureRows,
      };
    });

    return {
      ...sectionRow,
      analysisData: sectionOrderedAnalysisData,
      MAAnalysisData: sectionOrderedMAAnalysisData,
      subRows: sectionOrderedSubRows,
    };
  });
};
